import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { BcmApiService } from './bcm-api.service';
import { downloadFile } from '@shared/functions/download-file';
import {
    FromToRangeButton,
    GetFromToRangeDialogComponent
} from '@sharedComponents/dialogs/get-from-to-range-dialog/get-from-to-range-dialog.component';
import { BcmService } from '../../../bcm.service';
import { MatDialog } from '@angular/material/dialog';
import { format } from '@core/date.facade';
import { TranslationService } from '@core/translation/translation.service';
import { lastValueFrom, Observable } from 'rxjs';
import { startOfYear } from '@core/date.facade';
import {
    buildReportExportFileNameWithDateRange,
    normalizeTableExportFileName,
    sanitizeExcelWorksheetName,
    sanitizeFileNameBase,
} from '@modules/bcm/@core/state-management/table-export-filename.util';

@Injectable({
    providedIn: 'root'
})
export class BcmExportExcelApiService extends BcmApiService {

    constructor(private _matDialog: MatDialog,
                _http: HttpClient,
                _bcmService: BcmService) {
        super(_http, _bcmService);
    }

    exportAbsences(): void {
        this.export('absences', TranslationService.translate('reports.exports.absences'));
    }

    exportPiersCurrentView(searchTerm?: string): void {
        const piersName = TranslationService.translate('reports.exports.piers');
        const columns = [
            {name: TranslationService.translate('table.name'), key: 'handle'},
            {name: TranslationService.translate('table.widthMeters'), key: 'width'},
            {name: TranslationService.translate('table.note'), key: 'note'},
        ];
        let params = new HttpParams()
            .set('columns', JSON.stringify(columns))
            .set('workSheetName', piersName);
        if (searchTerm?.trim()) {
            params = params.set('q', searchTerm.trim());
        }
        this.getBlob('export/excel/table/piers', params)
            .subscribe(blob => downloadFile(blob, piersName, 'xlsx'));
    }

    exportPersons(): void {
        this.export('persons', TranslationService.translate('reports.exports.persons'));
    }

    exportPersonTenantRelations(): void {
        this.export('persons/tenant-relations', TranslationService.translate('reports.exports.personsRelations'));
    }

    exportPersonsAndCompanies(): void {
        this.export('persons-companies', TranslationService.translate('reports.exports.personsAndCompanies'));
    }

    exportCompanies(): void {
        this.export('companies', TranslationService.translate('reports.exports.companies'));
    }

    exportCompanyTenantRelations(): void {
        this.export('companies/tenant-relations', TranslationService.translate('reports.exports.companiesRelations'));
    }

    exportProducts(): void {
        this.export('products', TranslationService.translate('reports.exports.products'));
    }

    exportProductsWithCostCenter(): void {
        this.export('products-with-cost-center', TranslationService.translate('reports.exports.productsCostCenter'));
    }

    exportBoats(): void {
        this.export('boats', TranslationService.translate('boats.plural'));
    }

    exportWinterStorages(): void {
        this.export('winter-storages', TranslationService.translate('bcmWinterStorages.plural'));
    }

    exportBerths(): void {
        this.export('berths', TranslationService.translate('reports.exports.berths'));
    }

    async exportBerthsAssignments(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const range = await dialogRef
            .afterClosed()
            .toPromise();

        if (range === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', range?.from ? format(range?.from, 'yyyy-MM-dd') : '')
            .set('to', range?.to ? format(range.to, 'yyyy-MM-dd') : '');

        this.export(
            'berthsAssignments',
            TranslationService.translate('reports.exports.berthAssignments'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.berthAssignments'), range?.from, range?.to),
        );
    }

    exportElectricMeters(): void {
        this.export('electric-meters', TranslationService.translate('reports.exports.electricMeters'));
    }

    exportKeys(persons = false): void {
        this.export('keys' + (persons ? '/persons' : ''), TranslationService.translate('reports.exports.keys'));
    }

    exportSeasons(): void {
        this.export('seasons', TranslationService.translate('reports.exports.seasons'));
    }

    exportVouchers(): void {
        this.export('vouchers', TranslationService.translate('bcmVouchers.plural'));
    }

    async exportElectricMeterReadings(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const range = await dialogRef
            .afterClosed()
            .toPromise();

        if (range === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', range?.from ? format(range?.from, 'yyyy-MM-dd') : '')
            .set('to', range?.to ? format(range.to, 'yyyy-MM-dd') : '');

        this.export(
            'electric-meter-readings',
            TranslationService.translate('reports.exports.electricReadings'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.electricReadings'), range?.from, range?.to),
        );
    }

    async exportOpenInvoicePostions(): Promise<void> {
        this.export('invoicePositions', TranslationService.translate('reports.exports.invoicePositions'));
    }

    async exportAllInvoicePositionsWithPaymentType(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const now = new Date();
        dialogRef.componentInstance.allowTypeAll = false;
        dialogRef.componentInstance.fromFieldOptional = false;
        dialogRef.componentInstance.toFieldOptional = false;
        dialogRef.componentInstance.defaultType = FromToRangeButton.CurrentYear;
        dialogRef.componentInstance.fromDateDefault = startOfYear(now);
        dialogRef.componentInstance.fromDateMax = now;
        dialogRef.componentInstance.toDateDefault = now;
        dialogRef.componentInstance.toDateMax = now;
        dialogRef.componentInstance.types = [
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];

        const range = await lastValueFrom(dialogRef.afterClosed());

        if (range === false) {
            return;
        }

        const fileName = TranslationService.translate('reports.exports.allInvoicePositionsWithPaymentType');
        const params = new HttpParams()
            .set('from', range?.from ? format(range?.from, 'yyyy-MM-dd') : '')
            .set('to', range?.to ? format(range.to, 'yyyy-MM-dd') : '');

        this.export(
            'invoicePositions/with-payment-type',
            fileName,
            params,
            buildReportExportFileNameWithDateRange(fileName, range?.from, range?.to),
        );
    }

    async exportAllInvoicePostions(includeOpen: boolean): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const now = new Date();
        dialogRef.componentInstance.fromDateDefault = startOfYear(now);
        dialogRef.componentInstance.fromDateMax = now;
        dialogRef.componentInstance.toDateDefault = now;
        dialogRef.componentInstance.toDateMax = now;
        dialogRef.componentInstance.types = [
            FromToRangeButton.All,
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];

        const range = await dialogRef
            .afterClosed()
            .toPromise();

        if (range === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', range?.from ? format(range?.from, 'yyyy-MM-dd') : '')
            .set('to', range?.to ? format(range.to, 'yyyy-MM-dd') : '')
            .set('includeOpen', includeOpen ? 'true' : '');

        const fileName = includeOpen
            ? TranslationService.translate('reports.exports.allPositionsWithInvoice')
            : TranslationService.translate('reports.exports.allPositionsWithoutInvoice');

        this.export(
            'invoicePositions/all',
            fileName,
            params,
            buildReportExportFileNameWithDateRange(fileName, range?.from, range?.to),
        );
    }

    async exportAllSubscriptions(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        dialogRef.componentInstance.optionalInfoText =
            TranslationService.translate('reports.exports.subscriptionsExportInfo');
        dialogRef.componentInstance.types = [
            FromToRangeButton.All,
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];

        const range = await dialogRef
            .afterClosed()
            .toPromise();

        if (range === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', range?.from ? format(range?.from, 'yyyy-MM-dd') : '')
            .set('to', range?.to ? format(range.to, 'yyyy-MM-dd') : '');

        this.export(
            'subscriptions',
            TranslationService.translate('reports.exports.allSubscriptions'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.allSubscriptions'), range?.from, range?.to),
        );
    }

    async exportFinancialRecords(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const now = new Date();
        dialogRef.componentInstance.allowTypeAll = false;
        dialogRef.componentInstance.fromFieldOptional = false;
        dialogRef.componentInstance.toFieldOptional = false;
        dialogRef.componentInstance.fromDateDefault = startOfYear(now);
        dialogRef.componentInstance.fromDateMax = now;
        dialogRef.componentInstance.toDateDefault = now;
        dialogRef.componentInstance.toDateMax = now;
        dialogRef.componentInstance.types = [
            FromToRangeButton.All,
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];
        dialogRef.componentInstance.orderByFilters = [
            {orderBy: 'date', label: TranslationService.translate('reports.exports.sortByDocumentDate')},
            {orderBy: 'number', label: TranslationService.translate('reports.exports.sortByDocumentNumber')},
        ];
        dialogRef.componentInstance.orderByDefault = 'number';

        const formData = await dialogRef
            .afterClosed()
            .toPromise();

        if (formData === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', formData?.from ? format(formData?.from, 'yyyy-MM-dd') : '')
            .set('to', formData?.to ? format(formData.to, 'yyyy-MM-dd') : '')
            .set('orderBy', formData?.orderBy || '');

        this.export(
            'financial-records',
            TranslationService.translate('reports.exports.allVouchers'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.allVouchers'), formData?.from, formData?.to),
        );
    }

    async exportInvoices(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const now = new Date();
        dialogRef.componentInstance.allowTypeAll = false;
        dialogRef.componentInstance.fromFieldOptional = false;
        dialogRef.componentInstance.toFieldOptional = false;
        dialogRef.componentInstance.fromDateDefault = startOfYear(now);
        dialogRef.componentInstance.fromDateMax = now;
        dialogRef.componentInstance.toDateDefault = now;
        dialogRef.componentInstance.toDateMax = now;
        dialogRef.componentInstance.types = [
            FromToRangeButton.All,
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];
        dialogRef.componentInstance.orderByFilters = [
            {orderBy: 'date', label: TranslationService.translate('reports.exports.sortByInvoiceDate')},
            {orderBy: 'invoiceNumber', label: TranslationService.translate('reports.exports.sortByInvoiceNumber')},
        ];
        dialogRef.componentInstance.orderByDefault = 'invoiceNumber';

        const formData = await dialogRef
            .afterClosed()
            .toPromise();

        if (formData === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', formData?.from ? format(formData?.from, 'yyyy-MM-dd') : '')
            .set('to', formData?.to ? format(formData.to, 'yyyy-MM-dd') : '')
            .set('orderBy', formData?.orderBy || '');

        this.export(
            'invoices',
            TranslationService.translate('accounting.financialDocuments.invoices'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('accounting.financialDocuments.invoices'), formData?.from, formData?.to),
        );
    }

    async exportInvoicesSentByEmail(): Promise<void> {
        await this.exportInvoicesWithDateRange(
            'invoices/sent-by-email',
            TranslationService.translate('reports.exports.invoicesSentByEmail'),
        );
    }

    async exportInvoicesWithoutEmailSend(): Promise<void> {
        await this.exportInvoicesWithDateRange(
            'invoices/without-email-send',
            TranslationService.translate('reports.exports.invoicesWithoutEmailSend'),
        );
    }

    private async exportInvoicesWithDateRange(endpoint: string, fileName: string): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const now = new Date();
        dialogRef.componentInstance.allowTypeAll = false;
        dialogRef.componentInstance.fromFieldOptional = false;
        dialogRef.componentInstance.toFieldOptional = false;
        dialogRef.componentInstance.fromDateDefault = startOfYear(now);
        dialogRef.componentInstance.fromDateMax = now;
        dialogRef.componentInstance.toDateDefault = now;
        dialogRef.componentInstance.toDateMax = now;
        dialogRef.componentInstance.types = [
            FromToRangeButton.All,
            FromToRangeButton.LastYear,
            FromToRangeButton.CurrentYear,
            FromToRangeButton.LastMonth,
            FromToRangeButton.CurrentMonth,
            FromToRangeButton.LastWeek,
            FromToRangeButton.CurrentWeek,
            FromToRangeButton.Yesterday,
            FromToRangeButton.Today,
        ];

        const formData = await dialogRef
            .afterClosed()
            .toPromise();

        if (formData === false) {
            return;
        }

        const params = new HttpParams()
            .set('from', formData?.from ? format(formData?.from, 'yyyy-MM-dd') : '')
            .set('to', formData?.to ? format(formData.to, 'yyyy-MM-dd') : '');

        this.export(
            endpoint,
            fileName,
            params,
            buildReportExportFileNameWithDateRange(fileName, formData?.from, formData?.to),
        );
    }

    async exportOrders(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const range = await lastValueFrom(dialogRef.afterClosed(), {defaultValue: false});

        if (range === false) {
            return;
        }

        const from = range?.from ? format(range?.from, 'yyyy-MM-dd') : '';
        const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

        const params = new HttpParams()
            .set('from', from)
            .set('to', to);

        this.exportNew(
            'orders',
            TranslationService.translate('reports.exports.workOrders'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.workOrders'), range?.from, range?.to),
        );
    }

    exportCostCenters(): void {
        this.export('cost-centers', TranslationService.translate('reports.exports.costCenters'));
    }

    async exportPositionsByCostCenters(): Promise<void> {
        const dialogRef = this._matDialog.open(GetFromToRangeDialogComponent, {disableClose: true});

        const range = await lastValueFrom(dialogRef.afterClosed(), {defaultValue: false});

        if (range === false) {
            return;
        }

        const from = range?.from ? format(range?.from, 'yyyy-MM-dd') : '';
        const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

        const params = new HttpParams()
            .set('from', from)
            .set('to', to);

        this.export(
            'cost-center-positions',
            TranslationService.translate('reports.exports.invoicePositionsCostCenter'),
            params,
            buildReportExportFileNameWithDateRange(TranslationService.translate('reports.exports.invoicePositionsCostCenter'), range?.from, range?.to),
        );
    }

    private export(endpoint: string, translatedLabel: string, params = new HttpParams(), downloadFileName?: string): void {
        const fileName = downloadFileName ?? sanitizeFileNameBase(translatedLabel);
        const workSheetName = sanitizeExcelWorksheetName(translatedLabel);

        super.getBlob(
            `export/excel/${endpoint}`,
            params
                .set('workSheetName', workSheetName)
                .set('fileName', fileName)
        ).subscribe((file) => downloadFile(file, normalizeTableExportFileName(fileName), 'xlsx'));
    }

    private exportNew(endpoint: string, translatedLabel: string, params = new HttpParams(), downloadFileName?: string): void {
        const fileName = downloadFileName ?? sanitizeFileNameBase(translatedLabel);
        const workSheetName = sanitizeExcelWorksheetName(translatedLabel);

        super.getBlob(
            `export/${endpoint}/excel`,
            params
                .set('workSheetName', workSheetName)
                .set('fileName', fileName)
        ).subscribe((file) => downloadFile(file, normalizeTableExportFileName(fileName), 'xlsx'));
    }

    exportAndBookInvoicePositions(positionIds: number[], personId?: number, companyId?: number): Observable<HttpEvent<Blob>> {
        return super.postWithBlobResponse('export/csv/invoice-positions-with-factoring', {
            positionIds,
            personId,
            companyId,
        });
    }
}
